import { useState } from 'react';

import { AlertColor } from '@mui/material';

import { ValidationField, ValidationResult, formatValidationError, validateFields } from '../utils/validationUtils';

interface UsePreDialogValidationReturn {
  disabledButton: boolean;
  tooltipError: string;
  snackbarOpen: boolean;
  snackbarMessage: string;
  severity: AlertColor;
  setSnackbarOpen: (open: boolean) => void;
  openSnackbar: (message: string, severity: AlertColor) => void;
  validateAndOpen: (
    fetchAndValidate: () => Promise<{ fields: ValidationField[]; dealId: string | number; context?: string } | null>,
    onSuccess: () => void,
  ) => Promise<void>;
  resetValidation: () => void;
  setTooltipError: (error: string) => void;
  setDisabledButton: (disabled: boolean) => void;
}

const usePreDialogValidation = (): UsePreDialogValidationReturn => {
  const [disabledButton, setDisabledButton] = useState(false);
  const [tooltipError, setTooltipError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('error');

  const openSnackbar = (message: string, sev: AlertColor) => {
    setSnackbarMessage(message);
    setSeverity(sev);
    setSnackbarOpen(true);
  };

  const resetValidation = () => {
    setDisabledButton(false);
    setTooltipError('');
  };

  const validateAndOpen = async (
    fetchAndValidate: () => Promise<{ fields: ValidationField[]; dealId: string | number; context?: string } | null>,
    onSuccess: () => void,
  ) => {
    try {
      const validationData = await fetchAndValidate();

      // null means the fetchAndValidate handled its own error/validation
      if (validationData === null) return;

      const result: ValidationResult = validateFields(validationData.fields);
      const hasErrors = result.missing.length > 0 || result.invalidFormat.length > 0;

      if (hasErrors) {
        const errorMessage = formatValidationError(validationData.dealId, result, validationData.context);
        setTooltipError(errorMessage);
        setDisabledButton(true);
        openSnackbar(errorMessage, 'error');
        return;
      }

      // Validation passed
      resetValidation();
      onSuccess();
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Erreur lors de la validation';
      setTooltipError(message);
      setDisabledButton(false); // Allow retry
      openSnackbar(message, 'error');
    }
  };

  return {
    disabledButton,
    tooltipError,
    snackbarOpen,
    snackbarMessage,
    severity,
    setSnackbarOpen,
    openSnackbar,
    validateAndOpen,
    resetValidation,
    setTooltipError,
    setDisabledButton,
  };
};

export default usePreDialogValidation;
